import axios from 'axios'
import { Url } from './constantUrl'
import getHeaders from './getHeaders'
import * as types from './types'
import { errorHandler } from './error/errorHandler.action'
import { notify } from '../services/notification'
import getHeadersformdata from './getHeaderFormdata'

/**
 * Get DBT Rules List with pagination, search, and filters
 * @param {number} page - Current page number
 * @param {number} pageSize - Number of items per page
 * @param {string} query - Query string for search/filters (e.g., "&search=test&status=active")
 * @returns {Promise} Response with DBT rules list
 */
export const getDbtRulesListAction = (page, pageSize, query = '') => dispatch => {
  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_RULES_LIST}?page_size=${pageSize}&page=${page}${query}`;
  const options = {
    method: 'GET',
    headers: getHeaders(),
    url: url
  };

  return axios(options)
    .then(response => {
      if (response.status === 200) {
        dispatch({ type: types.GET_DBT_RULES_LIST, payload: response });
        return response;
      } else {
        return null;
      }
    })
    .catch(error => {
      errorHandler(error);
      throw error;
    });
};

/**
 * Get DBT Rule Details by ID
 * @param {string|number} ruleId - DBT Rule ID
 * @returns {Promise} Response with DBT rule details
 */
export const getDbtRuleDetailAction = (ruleId) => dispatch => {
  if (!ruleId) {
    notify.error('Rule ID is required');
    return Promise.reject(new Error('Rule ID is required'));
  }

  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_RULE_DETAIL}/${ruleId}`;
  const options = {
    method: 'GET',
    headers: getHeaders(),
    url: url
  };

  return axios(options)
    .then(response => {
      if (response.status === 200) {
        dispatch({ type: types.GET_DBT_RULE_DETAIL, payload: response });
        return response;
      } else {
        return null;
      }
    })
    .catch(error => {
      errorHandler(error);
      throw error;
    });
};

/**
 * Create new DBT Rule
 * @param {Object} payload - DBT Rule data
 * @returns {Promise} Response after creating DBT rule
 */
export const createDbtRuleAction = (payload) => async (dispatch) => {
  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_RULE_CREATE}`;
  const options = {
    method: 'POST',
    headers: getHeaders(),
    url: url,
    data: payload
  };

  try {
    const response = await axios(options);
    if (response?.status === 200 || response?.status === 201) {
      dispatch({ type: types.POST_CREATE_DBT_RULE, payload: response });
      notify.success(response?.data?.message || 'DBT Rule created successfully');
      return response;
    } else {
      return null;
    }
  } catch (error) {
    if (error?.response) {
      if (error?.response?.status === 400) {
        const errorData = error?.response?.data?.errors;
        if (errorData) {
          // Handle validation errors
          if (Array.isArray(errorData)) {
            errorData.forEach(err => {
              const errorMessage = typeof err === 'string' ? err : JSON.stringify(err);
              notify.error(errorMessage);
            });
          } else if (typeof errorData === 'object') {
            Object.keys(errorData).forEach(key => {
              const value = errorData[key];
              let message;

              if (Array.isArray(value)) {
                message = value[0];
              } else if (typeof value === 'object' && value !== null) {
                // If the value is an object, convert it to a readable string
                message = JSON.stringify(value);
              } else {
                message = value;
              }

              notify.error(`${key}: ${message}`);
            });
          } else if (typeof errorData === 'string') {
            notify.error(errorData);
          }
        }
        return error.response;
      } else {
        errorHandler(error);
      }
    } else {
      errorHandler(error);
    }
    throw error;
  }
};

/**
 * Update existing DBT Rule
 * @param {string|number} ruleId - DBT Rule ID
 * @param {Object} payload - Updated DBT Rule data
 * @returns {Promise} Response after updating DBT rule
 */
export const updateDbtRuleAction = (ruleId, payload) => async (dispatch) => {
  if (!ruleId) {
    notify.error('Rule ID is required');
    return Promise.reject(new Error('Rule ID is required'));
  }

  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_RULE_UPDATE}/${ruleId}`;
  const options = {
    method: 'PUT',
    headers: getHeaders(),
    url: url,
    data: payload
  };

  try {
    const response = await axios(options);
    if (response?.status === 200 || response?.status === 201) {
      dispatch({ type: types.PUT_UPDATE_DBT_RULE, payload: response });
      notify.success(response?.data?.message || 'DBT Rule updated successfully');
      return response;
    } else {
      return null;
    }
  } catch (error) {
    if (error?.response) {
      if (error?.response?.status === 400) {
        const errorData = error?.response?.data?.errors;

        if (errorData) {
          // Handle validation errors
          if (Array.isArray(errorData)) {
            errorData.forEach(err => {
              const errorMessage = typeof err === 'string' ? err : JSON.stringify(err);
              notify.error(errorMessage);
            });
          } else if (typeof errorData === 'object') {
            Object.keys(errorData).forEach(key => {
              const value = errorData[key];
              let message;

              if (Array.isArray(value)) {
                message = value[0];
              } else if (typeof value === 'object' && value !== null) {
                // If the value is an object, convert it to a readable string
                message = JSON.stringify(value);
              } else {
                message = value;
              }

              notify.error(`${key}: ${message}`);
            });
          } else if (typeof errorData === 'string') {
            notify.error(errorData);
          }
        }
        return error.response;
      } else {
        errorHandler(error);
      }
    } else {
      errorHandler(error);
    }
    throw error;
  }
};

/**
 * Delete DBT Rule
 * @param {string|number} ruleId - DBT Rule ID
 * @returns {Promise} Response after deleting DBT rule
 */
export const deleteDbtRuleAction = (ruleId) => async (dispatch) => {
  if (!ruleId) {
    notify.error('Rule ID is required');
    return Promise.reject(new Error('Rule ID is required'));
  }

  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_RULE_DELETE}/${ruleId}`;
  const options = {
    method: 'DELETE',
    headers: getHeaders(),
    url: url
  };

  try {
    const response = await axios(options);
    if (response?.status === 200 || response?.status === 204) {
      dispatch({ type: types.DELETE_DBT_RULE, payload: { ruleId } });
      notify.success(response?.data?.message || 'DBT Rule deleted successfully');
      return response;
    } else {
      return null;
    }
  } catch (error) {
    errorHandler(error);
    throw error;
  }
};

/**
 * Download DBT Rules Excel file
 * @param {string} query - Optional query string for filters
 * @returns {Promise} Blob for Excel file download
 */
export const downloadDbtRulesExcelAction = (query = '') => async (dispatch) => {
  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_RULES_EXCEL}${query ? `?${query}` : ''}`;
  const headers = getHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (response.status === 200) {
      const blob = await response.blob();
      return blob;
    } else {
      notify.error('Failed to download file');
      return null;
    }
  } catch (error) {
    errorHandler(error);
    throw error;
  }
};

/**
 * Get Cluster List
 * @param {string} searchQuery - Optional search query (e.g., cluster=L)
 * @returns {Promise} Response with cluster list
 */
export const getClusterListAction = (searchQuery = '') => dispatch => {
  // const url = `${process.env.REACT_APP_API_URL}${Url.CLUSTER_LIST}${searchQuery ? `?cluster=${searchQuery}` : ''}`;
  const url = `${process.env.REACT_APP_API_URL}${Url.CLUSTER_LIST}`;

  const options = {
    method: 'GET',
    headers: getHeaders(),
    url: url
  };

  return axios(options)
    .then(response => {
      if (response.status === 200) {
        return response;
      } else {
        return null;
      }
    })
    .catch(error => {
      errorHandler(error);
      throw error;
    });
};

/**
 * Get Cluster Part List
 * @param {string} clusterName - Cluster name to fetch parts for
 * @returns {Promise} Response with cluster part details
 */
export const getClusterPartListAction = (clusterName) => dispatch => {
  if (!clusterName) {
    notify.error('Cluster name is required');
    return Promise.reject(new Error('Cluster name is required'));
  }

  const url = `${process.env.REACT_APP_API_URL}${Url.CLUSTER_PART_LIST}?cluster=${encodeURIComponent(clusterName)}`;
  const options = {
    method: 'GET',
    headers: getHeaders(),
    url: url
  };

  return axios(options)
    .then(response => {
      if (response.status === 200) {
        return response;
      } else {
        return null;
      }
    })
    .catch(error => {
      errorHandler(error);
      throw error;
    });
};

/**
 * Search Region by Type and Value
 * @param {string} regionType - Region type (zone/state/aps/pincode)
 * @param {string} searchValue - Search query (minimum 3 characters)
 * @returns {Promise} Response with matching regions
 */
export const getRegionSearchAction = (regionType, searchValue) => dispatch => {
  if (!regionType || !searchValue) {
    return Promise.reject(new Error('Region type and search value are required'));
  }

  if (searchValue.length < 3) {
    return Promise.resolve({ status: 200, data: { success: true, data: [] } });
  }

  const url = `${process.env.REACT_APP_API_URL}${Url.REGION_SEARCH}?region_type=${encodeURIComponent(regionType)}&region_value=${encodeURIComponent(searchValue)}`;
  const options = {
    method: 'GET',
    headers: getHeaders(),
    url: url
  };

  return axios(options)
    .then(response => {
      if (response.status === 200) {
        return response;
      } else {
        return null;
      }
    })
    .catch(error => {
      errorHandler(error);
      throw error;
    });
};

/**
 * Get DBT Invoice Scan Status List with pagination
 * @param {number} page - Current page number
 * @param {number} pageSize - Number of items per page
 * @param {string} query - Query string for search/filters (e.g., "&customer_code=123&aps_code=456")
 * @returns {Promise} Response with DBT invoice scan status list
 */
export const getDbtInvoiceScanStatusAction = (page, pageSize, query = '') => dispatch => {
  const url = `${process.env.REACT_APP_API_URL}${Url.DBT_INVOICE_SCAN_STATUS}?page_size=${pageSize}&page=${page}${query}`;
  const options = {
    method: 'GET',
    headers: getHeaders(),
    url: url
  };

  return axios(options)
    .then(response => {
      if (response.status === 200) {
        dispatch({ type: types.GET_DBT_INVOICE_SCAN_STATUS, payload: response });
        return response;
      } else {
        return null;
      }
    })
    .catch(error => {
      errorHandler(error);
      throw error;
    });
};

/**
 * Download DBT Invoice Scan Status Excel file
 * @param {string} query - Query string for filters (e.g., "&customer_code=123&aps_type=primary")
 * @param {string|number} reportId - Dynamic report ID (defaults to 4)
 * @returns {Promise} Response with success message
 */
export const downloadDbtInvoiceScanStatusExcelAction = (query = '', reportId = 4) => async (dispatch) => {
  const url = process.env.REACT_APP_API_URL + Url.REPORTS + reportId + "?download=true" + (query.startsWith('&') ? query : (query ? '&' + query : ''));
  const headers = getHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (response.status === 200) {
      const data = await response.json();
      return { status: 200, data };
    } else {
      const errorData = await response.json();
      notify.error(errorData?.message|| errorData?.error || 'Failed to download file');
      return null;
    }
  } catch (error) {
    errorHandler(error);
  }
};

//DBT IsActive View PUT  API
export const DbtIsActiveAction = (params = {}, id) => dispatch => {
  const url = process.env.REACT_APP_API_URL + Url.ACTIVE_DBT + "/" + id
  let options = Object.assign({ method: 'PUT' }, params ? { data: params } : null)
  options.headers = getHeadersformdata()
  options.url = url
  return axios(options).then(resp => {
    if (resp.status === 200 || resp.status === 201) {
      dispatch({ type: types.PUT_ACTIVE_BANNER, payload: resp })
      return resp
    } else { return null }
  }).catch(error => {
    if (error?.response?.status === 400) {
      const errorMessage = error.response.data.msg;
      if (errorMessage) {
        notify.error(errorMessage);
      } else {
        errorHandler(error)
      }
    }
  })
}
